import React, { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

export default function CustomCursor() {
  const cursorX = useMotionValue(-100);
  const cursorY = useMotionValue(-100);
  const [isHovering, setIsHovering] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const isTouchDevice = useRef(false);
  const rafId = useRef(null);

  const springConfig = { damping: 25, stiffness: 300, mass: 0.5 };
  const x = useSpring(cursorX, springConfig);
  const y = useSpring(cursorY, springConfig);

  useEffect(() => {
    isTouchDevice.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice.current) return;

    // Throttled mousemove via rAF (30fps instead of 60+)
    let lastX = -100, lastY = -100;
    const move = (e) => {
      lastX = e.clientX;
      lastY = e.clientY;
      if (!rafId.current) {
        rafId.current = requestAnimationFrame(() => {
          cursorX.set(lastX);
          cursorY.set(lastY);
          rafId.current = null;
        });
      }
    };

    // Event delegation — no MutationObserver needed
    const handleOver = (e) => {
      if (e.target.closest('a, button, [data-cursor-hover]')) {
        setIsHovering(true);
      }
    };
    const handleOut = (e) => {
      if (e.target.closest('a, button, [data-cursor-hover]')) {
        setIsHovering(false);
      }
    };

    const handleEnter = () => setIsHidden(false);
    const handleLeave = () => setIsHidden(true);

    window.addEventListener('mousemove', move, { passive: true });
    document.addEventListener('mouseover', handleOver, { passive: true });
    document.addEventListener('mouseout', handleOut, { passive: true });
    document.addEventListener('mouseenter', handleEnter);
    document.addEventListener('mouseleave', handleLeave);

    return () => {
      window.removeEventListener('mousemove', move);
      document.removeEventListener('mouseover', handleOver);
      document.removeEventListener('mouseout', handleOut);
      document.removeEventListener('mouseenter', handleEnter);
      document.removeEventListener('mouseleave', handleLeave);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [cursorX, cursorY]);

  if (isTouchDevice.current) return null;

  return (
    <>
      <motion.div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          x,
          y,
          width: isHovering ? 48 : 12,
          height: isHovering ? 48 : 12,
          borderRadius: '50%',
          border: `2px solid ${isHovering ? 'rgba(220,38,38,0.8)' : 'rgba(220,38,38,0.5)'}`,
          backgroundColor: isHovering ? 'rgba(220,38,38,0.1)' : 'transparent',
          pointerEvents: 'none',
          zIndex: 99999,
          transform: 'translate(-50%, -50%)',
          opacity: isHidden ? 0 : 1,
          transition: 'width 0.3s, height 0.3s, border-color 0.3s, background-color 0.3s, opacity 0.3s',
          mixBlendMode: 'difference',
        }}
      />
      <style>{`
        @media (pointer: coarse) { .custom-cursor-dot { display: none !important; } }
        * { cursor: none !important; }
        @media (pointer: coarse) { * { cursor: auto !important; } }
      `}</style>
    </>
  );
}
